import { enableStaticRendering, Provider, inject, observer } from 'mobx-react';
import ErrorPage from 'next/error';

import isServer from './is-server.js';
import hasAnyRole from './has-any-role.js';
import loadStoreContext, { readSession } from './load-store-context.js';
import { createStores, restoreStores, toStoreName } from './store-registry.js';
import { ADMIN_ROLES } from './constants.js';

enableStaticRendering(isServer);

function isForbidden(session, requiredRoles) {
  if (!session.token) return false;
  if (!requiredRoles.length) return false;

  return !hasAnyRole(session.roles, requiredRoles);
}

async function loadPageProps(Page, context) {
  if (typeof Page.getInitialProps !== 'function') return {};

  return Page.getInitialProps(context);
}

export default function withStore(...Stores) {
  return (Page, requiredRoles = ADMIN_ROLES) => {
    const ConnectedPage = inject(...Stores.map(toStoreName))(observer(Page));

    function PageWithStores(props) {
      if (props.forbidden) return <ErrorPage statusCode={403} />;

      const stores = restoreStores(Stores, props.stores);

      return (
        <Provider {...stores}>
          <ConnectedPage {...props} />
        </Provider>
      );
    }

    PageWithStores.displayName = `withStore(${Page.displayName || Page.name})`;

    PageWithStores.getInitialProps = async (context) => {
      const session = readSession(context);

      if (isForbidden(session, requiredRoles)) {
        if (context.res) context.res.statusCode = 403;

        return { forbidden: true };
      }

      const stores = createStores(Stores, await loadStoreContext(session));
      const pageProps = await loadPageProps(Page, { ...context, stores });

      return { stores, ...pageProps };
    };

    return PageWithStores;
  };
}
